import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useRef,
} from "react";
import { Snackbar, Button, Box, Typography } from "@mui/material";
import SettingsContext from "src/context/SettingsContext";

const SnackbarContext = createContext();
export const useSnackbar = () => useContext(SnackbarContext);

export const SnackbarProvider = ({ children }) => {
  const themeSettings = useContext(SettingsContext);

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    onRefresh: null,
    durationMs: 30000,
  });

  // ✅ Use ref instead of state (so refresh callback is always current)
  const pageRefreshRef = useRef(null);

  const registerPageRefresh = useCallback((fn) => {
    pageRefreshRef.current = fn;
  }, []);

  const showSnackbar = useCallback((message, onRefresh, durationMs = 30000) => {
    const combinedRefresh = () => {
      if (onRefresh) {
        onRefresh();
      } else if (pageRefreshRef.current) {
        pageRefreshRef.current(); // ✅ this now always points to latest function
      }
    };

    setSnackbar({
      open: true,
      message,
      onRefresh: combinedRefresh,
      durationMs,
    });
  }, []);

  const handleClose = (_, reason) => {
    if (reason === "clickaway") return;
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  const isLight = themeSettings?.settings?.theme === "LIGHT";
  const bgColor = isLight ? "#FFFFFF" : "#0F0E14";
  const textColor = isLight ? "#000000" : "#FFFFFF";
  const borderColor = isLight ? "#E0E0E0" : "#3A3940";
  const refreshGradient = isLight
    ? "linear-gradient(180deg, rgb(95,81,222) 0%, rgb(173,93,211) 100%)"
    : "linear-gradient(180deg, rgb(100,86,230) 0%, rgb(190,100,230) 100%)";

  return (
    <SnackbarContext.Provider value={{ showSnackbar, registerPageRefresh }}>
      {children}

      <Snackbar
        open={snackbar.open}
        onClose={handleClose}
        autoHideDuration={snackbar.durationMs}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        sx={{
          "& .MuiPaper-root": {
            backgroundColor: bgColor,
            color: textColor,
            boxShadow: isLight
              ? "0px 4px 12px rgba(0,0,0,0.15)"
              : "0px 4px 20px rgba(255,255,255,0.05)",
            borderRadius: "12px",
            padding: "16px",
            minWidth: "320px",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            border: `1px solid ${borderColor}`,
          },
        }}
        message={
          <Box>
            <Typography
              sx={{
                fontWeight: 600,
                fontSize: "15px",
                fontFamily: '"Manrope", Helvetica',
                color: textColor,
              }}
            >
              {snackbar.message}
            </Typography>
          </Box>
        }
        action={
          <>
            {snackbar.onRefresh && (
              <Button
                size="small"
                variant="contained"
                sx={{
                  background: refreshGradient,
                  textTransform: "none",
                  fontWeight: 600,
                  borderRadius: "8px",
                  mr: 1,
                  color: "#FFF",
                  "&:hover": {
                    background: isLight
                      ? "linear-gradient(180deg, rgb(85,70,210) 0%, rgb(160,85,200) 100%)"
                      : "linear-gradient(180deg, rgb(90,75,210) 0%, rgb(175,90,210) 100%)",
                  },
                }}
                onClick={() => {
                  snackbar.onRefresh?.();
                  handleClose();
                }}
              >
                Refresh
              </Button>
            )}

            <Button
              size="small"
              variant="outlined"
              sx={{
                color: textColor,
                textTransform: "none",
                fontWeight: 600,
                borderRadius: "8px",
                borderColor: borderColor,
                "&:hover": {
                  borderColor: isLight ? "#999" : "#B8B8B8",
                },
              }}
              onClick={handleClose}
            >
              Dismiss
            </Button>
          </>
        }
      />
    </SnackbarContext.Provider>
  );
};
